import { isElectron } from '../electron';
import { openCacheDb, STORE } from './db';

export interface CacheEntry {
  key: string;
  ns: string;
  status: number;
  data: unknown;
  storedAt: number;
  bytes: number;
}

/**
 * Electron ships with real disk behind it; the browser build lives inside a
 * shared origin quota and gets a third.
 */
export const DEFAULT_MAX_BYTES = (isElectron ? 150 : 50) * 1024 * 1024;

function tx(db: IDBDatabase, mode: IDBTransactionMode): IDBObjectStore {
  return db.transaction(STORE, mode).objectStore(STORE);
}

function wrap<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function readCache(key: string): Promise<CacheEntry | null> {
  try {
    const db = await openCacheDb();
    const entry = await wrap(tx(db, 'readonly').get(key) as IDBRequest<CacheEntry | undefined>);
    return entry ?? null;
  } catch {
    return null;
  }
}

export async function writeCache(input: {
  key: string;
  ns: string;
  status: number;
  data: unknown;
}): Promise<void> {
  try {
    let bytes = 0;
    try {
      bytes = JSON.stringify(input.data)?.length ?? 0;
    } catch {
      return; // not serialisable — nothing worth storing
    }
    const db = await openCacheDb();
    const entry: CacheEntry = { ...input, storedAt: Date.now(), bytes };
    await wrap(tx(db, 'readwrite').put(entry) as IDBRequest);
  } catch {
    /* cache writes are best-effort */
  }
}

export async function clearNamespace(ns: string): Promise<void> {
  try {
    const db = await openCacheDb();
    // Read and delete in SEPARATE transactions. An IndexedDB transaction
    // auto-commits once the microtask queue drains, so a transaction that is
    // awaited mid-flight is already inactive by the time the deletes run.
    const keys = await wrap(
      tx(db, 'readonly').index('ns').getAllKeys(ns) as IDBRequest<IDBValidKey[]>,
    );
    if (!keys.length) return;
    const store = tx(db, 'readwrite');
    for (const key of keys) {
      store.delete(key);
    }
  } catch {
    /* best-effort */
  }
}

export async function clearForeignNamespaces(keepNs: string): Promise<void> {
  try {
    const db = await openCacheDb();
    const all = await wrap(tx(db, 'readonly').getAll() as IDBRequest<CacheEntry[]>);
    const doomed = all.filter((entry) => entry.ns !== keepNs);
    if (!doomed.length) return;
    const store = tx(db, 'readwrite');
    for (const entry of doomed) {
      store.delete(entry.key);
    }
  } catch {
    /* best-effort */
  }
}

export async function enforceSizeCap(maxBytes: number = DEFAULT_MAX_BYTES): Promise<void> {
  try {
    const db = await openCacheDb();
    const all = await wrap(tx(db, 'readonly').getAll() as IDBRequest<CacheEntry[]>);
    let total = all.reduce((sum, e) => sum + (e.bytes ?? 0), 0);
    if (total <= maxBytes) return;

    const oldestFirst = [...all].sort((a, b) => a.storedAt - b.storedAt);
    const store = tx(db, 'readwrite');
    for (const entry of oldestFirst) {
      if (total <= maxBytes) break;
      store.delete(entry.key);
      total -= entry.bytes ?? 0;
    }
  } catch {
    /* best-effort */
  }
}
